import { Component, signal, inject, OnInit, effect, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';

import { Auth } from '@core/services';
import { UserRole } from '@core/interfaces/role.interface';
import { History as HistoryService } from '@modules/history/services/history';
import { Reseller as ResellerService } from '@modules/reseller/services/reseller';
import { SubReseller as SubResellerService } from '@modules/sub-reseller/services/sub-reseller';
import { DateRangePicker } from '@shared/date-range-picker/date-range-picker';
import { DataNotFound } from '@shared/data-not-found/data-not-found';
import { Loader } from '@shared/loader/loader';
import { Pagination } from '@shared/pagination/pagination';
import { Title, Toaster } from '@shared/services';

interface UserFilterOption {
  _id: string;
  label: string;
}

@Component({
  selector: 'app-history',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    Pagination,
    DateRangePicker,
    DataNotFound,
    Loader
  ],
  templateUrl: './history.html',
  styleUrl: './history.scss'
})
export class History implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(Auth);
  private historyService = inject(HistoryService);
  private resellerService = inject(ResellerService);
  private subResellerService = inject(SubResellerService);
  private titleService = inject(Title);
  private toasterService = inject(Toaster);

  isLoading = signal(false);
  selectedResellerId = signal<string | null>(null);
  selectedSubResellerId = signal<string | null>(null);
  userOptions = signal<UserFilterOption[]>([]);
  subResellerOptions = signal<UserFilterOption[]>([]);
  statuses = signal(['Cancelled', 'Delivered', 'Failed', 'Pending', 'Sent', 'Undelivered']);
  totalItems = signal(0);
  pageSize = signal(5);
  currentPage = signal(1);
  searchTerm = signal('');
  activeStatus = signal<string | null>(null);
  messageList: any = signal([]);

  dateRange: FormGroup = this.fb.group({
    range: [null]
  });

  isAdmin = computed(() => (this.authService.user?.role?.name ?? '') === UserRole.ADMIN);
  isReseller = computed(() => (this.authService.user?.role?.name ?? '') === UserRole.RESELLER);

  filterColumnCount = computed(() => {
    if (this.isAdmin()) {
      return 3;
    }
    if (this.isReseller()) {
      return 2;
    }
    return 1;
  });

  // Convert form value changes to a signal for reactivity
  private rangeValue = toSignal(this.dateRange.get('range')!.valueChanges, { initialValue: null });

  constructor() {
    effect(() => {
      // Accessing signals to track them
      this.activeStatus();
      this.currentPage();
      this.pageSize();
      this.rangeValue();
      this.searchTerm();
      this.selectedResellerId();
      this.selectedSubResellerId();

      this.messageHistory();
    });
  }

  ngOnInit() {
    this.getUserDetails();
    this.titleService.setTitle('History');
    this.loadUserOptions();
    this.loadSubResellerOptions();
  }

  loadUserOptions(): void {
    if (!this.isAdmin()) {
      return;
    }

    this.resellerService
      .getResellers({ type: UserRole.RESELLER, page: 1, pageSize: 500 })
      .subscribe({
        next: (response) => {
          const list = response?.data ?? [];
          this.userOptions.set(this.mapUserOptions(list));
        },
        error: (error) => {
          this.toasterService.errorAlert(
            error.error?.message || 'Failed to load resellers for filter'
          );
        }
      });
  }

  loadSubResellerOptions(): void {
    if (!this.isReseller() && !this.isAdmin()) {
      return;
    }

    this.subResellerService.getSubResellers({ type: UserRole.SUB_RESELLER }).subscribe({
      next: (response) => {
        const list = response?.data ?? [];
        this.subResellerOptions.set(this.mapUserOptions(list));
      },
      error: (error) => {
        this.toasterService.errorAlert(
          error.error?.message || 'Failed to load sub resellers for filter'
        );
      }
    });
  }

  onUserSelect(userId: string): void {
    const id = userId || null;
    this.selectedResellerId.set(id);
    if (id) {
      this.selectedSubResellerId.set(null);
    }
    this.currentPage.set(1);
  }

  onSubResellerSelect(userId: string): void {
    const id = userId || null;
    this.selectedSubResellerId.set(id);
    if (id) {
      this.selectedResellerId.set(null);
    }
    this.currentPage.set(1);
  }

  getUserDetails() {
    this.historyService.userDetails().subscribe({
      next: (response: any) => {
        this.authService.user = response.data;
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error?.message || 'Failed to fetch user details');
      }
    });
  }

  messageHistory() {
    this.isLoading.set(true);
    const queryParams: any = {
      page: this.currentPage(),
      pageSize: this.pageSize()
    };

    if (this.activeStatus()) {
      queryParams.status = this.activeStatus();
    }

    if (this.searchTerm()) {
      queryParams.search = this.searchTerm();
    }

    const range = this.dateRange.get('range')?.value;
    if (range) {
      queryParams.startDate = range.start;
      queryParams.endDate = range.end;
    }

    this.appendUserFilter(queryParams);

    this.historyService.getMessageHistory(queryParams).subscribe({
      next: (response: any) => {
        this.messageList.set(response.data);
        this.totalItems.set(response.pagination.total);
        this.isLoading.set(false);
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error?.message || 'Failed to fetch history');
        this.isLoading.set(false);
      }
    });
  }

  setActiveStatus(status: string) {
    this.activeStatus.set(status);
    this.currentPage.set(1);
  }

  isFilterApplied() {
    return (
      this.activeStatus() !== null ||
      this.dateRange.get('range')?.value !== null ||
      this.searchTerm() !== '' ||
      this.selectedResellerId() !== null ||
      this.selectedSubResellerId() !== null
    );
  }

  clearFilter() {
    this.activeStatus.set(null);
    this.selectedResellerId.set(null);
    this.selectedSubResellerId.set(null);
    this.dateRange.reset({ range: null });
    this.currentPage.set(1);
    this.searchTerm.set('');
  }

  onPageChange(page: number) {
    this.currentPage.set(page);
  }

  onPageSizeChange(size: number) {
    this.pageSize.set(size);
    this.currentPage.set(1);
  }

  onSearch(searchTerm: string) {
    this.searchTerm.set(searchTerm);
    this.currentPage.set(1);
  }

  downloadHistory() {
    const queryParams: any = {
      page: this.currentPage(),
      pageSize: this.pageSize()
    };

    if (this.activeStatus()) {
      queryParams.status = this.activeStatus();
    }

    if (this.searchTerm()) {
      queryParams.search = this.searchTerm();
    }

    const range = this.dateRange.get('range')?.value;
    if (range) {
      queryParams.startDate = range.start;
      queryParams.endDate = range.end;
    }

    this.appendUserFilter(queryParams);

    this.historyService.downloadMessageHistory(queryParams).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'History.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.toasterService.successAlert('History downloaded successfully');
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error?.message || 'Failed to download history');
      }
    });
  }

  private appendUserFilter(queryParams: Record<string, unknown>): void {
    if (this.selectedSubResellerId()) {
      queryParams['user'] = this.selectedSubResellerId();
    } else if (this.selectedResellerId()) {
      queryParams['user'] = this.selectedResellerId();
    }
  }

  private mapUserOptions(list: any[]): UserFilterOption[] {
    return list
      .map((item) => {
        const id = item._id ?? item.id ?? item.userId;
        if (!id) return null;
        const label =
          `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim() || item.email || 'Unknown user';
        return { _id: String(id), label };
      })
      .filter((item): item is UserFilterOption => item !== null);
  }
}
